/**
 * キッチンプリンター設定コンポーネント
 * 調理場への注文伝票自動出力を管理
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Print,
  Kitchen,
  LocalBar,
  Restaurant,
  Refresh,
  CheckCircle,
  Error as ErrorIcon,
} from '@mui/icons-material';
import api from '../../services/api';

// プリンタータイプ
const PRINTER_TYPES = [
  { id: 'kitchen', name: 'キッチン（調理場）', icon: <Kitchen /> },
  { id: 'drink', name: 'ドリンクバー', icon: <LocalBar /> },
  { id: 'dessert', name: 'デザート', icon: <Restaurant /> },
  { id: 'takeout', name: 'テイクアウト', icon: <Restaurant /> },
];

// 接続方式
const CONNECTION_TYPES = [
  { id: 'lan', name: 'LAN（有線）' },
  { id: 'wifi', name: 'Wi-Fi' },
  { id: 'bluetooth', name: 'Bluetooth' },
  { id: 'usb', name: 'USB' },
];

// プリンターモデル
const PRINTER_MODELS = [
  { id: 'star_mc_print3', name: 'Star mC-Print3', width: 80 },
  { id: 'epson_tm_m30', name: 'Epson TM-m30', width: 80 },
  { id: 'seiko_rp_f10', name: 'SII RP-F10', width: 80 },
  { id: 'star_tsp100', name: 'Star TSP100', width: 58 },
  { id: 'generic_58mm', name: '汎用 58mm', width: 58 },
  { id: 'generic_80mm', name: '汎用 80mm', width: 80 },
];

const KitchenPrinterSettings = () => {
  const { storeId } = useParams();
  const [printers, setPrinters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [routingRules, setRoutingRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [testingPrinter, setTestingPrinter] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    type: 'kitchen',
    model: 'star_mc_print3',
    connectionType: 'lan',
    ipAddress: '',
    port: 9100,
    macAddress: '',
    isActive: true,
    printCopies: 1,
    fontSize: 'medium',
    showPrice: false,
    showOrderNumber: true,
    soundAlert: true,
  });

  // プリンター一覧取得
  const fetchPrinters = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/stores/${storeId}/kitchen-printers`);
      setPrinters(response.data || []);
    } catch (err) {
      console.error('キッチンプリンターの取得に失敗:', err);
      // デモデータ
      setPrinters([
        {
          id: 1,
          name: 'キッチンプリンター1',
          type: 'kitchen',
          model: 'star_mc_print3',
          connectionType: 'lan',
          ipAddress: '192.168.1.100',
          port: 9100,
          isActive: true,
          status: 'online',
          lastPrint: '2026-01-09 12:30:45',
        },
        {
          id: 2,
          name: 'ドリンクプリンター',
          type: 'drink',
          model: 'epson_tm_m30',
          connectionType: 'wifi',
          ipAddress: '192.168.1.101',
          port: 9100,
          isActive: true,
          status: 'online',
          lastPrint: '2026-01-09 12:28:00',
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  // カテゴリ一覧取得
  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get(`/api/stores/${storeId}/categories`);
      setCategories(response.data || []);
    } catch (err) {
      // デモデータ
      setCategories([
        { id: 1, name: 'フード' },
        { id: 2, name: 'ドリンク' },
        { id: 3, name: 'デザート' },
        { id: 4, name: 'アルコール' },
        { id: 5, name: 'サイドメニュー' },
      ]);
    }
  }, [storeId]);

  // ルーティング設定取得
  const fetchRoutingRules = useCallback(async () => {
    try {
      const response = await api.get(`/api/stores/${storeId}/printer-routing`);
      setRoutingRules(response.data || []);
    } catch (err) {
      // デモデータ
      setRoutingRules([
        { categoryId: 1, categoryName: 'フード', printerId: 1, printerName: 'キッチンプリンター1' },
        { categoryId: 2, categoryName: 'ドリンク', printerId: 2, printerName: 'ドリンクプリンター' },
        { categoryId: 4, categoryName: 'アルコール', printerId: 2, printerName: 'ドリンクプリンター' },
      ]);
    }
  }, [storeId]);

  useEffect(() => {
    fetchPrinters();
    fetchCategories();
    fetchRoutingRules();
  }, [fetchPrinters, fetchCategories, fetchRoutingRules]);

  // ダイアログを開く
  const handleOpenDialog = (printer = null) => {
    if (printer) {
      setEditingPrinter(printer);
      setFormData({
        name: printer.name || '',
        type: printer.type || 'kitchen',
        model: printer.model || 'star_mc_print3',
        connectionType: printer.connectionType || 'lan',
        ipAddress: printer.ipAddress || '',
        port: printer.port || 9100,
        macAddress: printer.macAddress || '',
        isActive: printer.isActive !== false,
        printCopies: printer.printCopies || 1,
        fontSize: printer.fontSize || 'medium',
        showPrice: printer.showPrice || false,
        showOrderNumber: printer.showOrderNumber !== false,
        soundAlert: printer.soundAlert !== false,
      });
    } else {
      setEditingPrinter(null);
      setFormData({
        name: '',
        type: 'kitchen',
        model: 'star_mc_print3',
        connectionType: 'lan',
        ipAddress: '',
        port: 9100,
        macAddress: '',
        isActive: true,
        printCopies: 1,
        fontSize: 'medium',
        showPrice: false,
        showOrderNumber: true,
        soundAlert: true,
      });
    }
    setDialogOpen(true);
  };

  // プリンター保存
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      if (!formData.name.trim()) {
        setError('プリンター名を入力してください');
        return;
      }

      if (formData.connectionType === 'lan' || formData.connectionType === 'wifi') {
        if (!formData.ipAddress.trim()) {
          setError('IPアドレスを入力してください');
          return;
        }
      }

      const data = {
        ...formData,
        storeId,
      };

      if (editingPrinter) {
        await api.put(`/api/stores/${storeId}/kitchen-printers/${editingPrinter.id}`, data);
      } else {
        await api.post(`/api/stores/${storeId}/kitchen-printers`, data);
      }

      setSuccess(true);
      setDialogOpen(false);
      fetchPrinters();
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('保存に失敗しました: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  // プリンター削除
  const handleDelete = async (printer) => {
    if (!window.confirm(`「${printer.name}」を削除しますか？`)) return;

    try {
      await api.delete(`/api/stores/${storeId}/kitchen-printers/${printer.id}`);
      fetchPrinters();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('削除に失敗しました');
    }
  };

  // テスト印刷
  const handleTestPrint = async (printer) => {
    try {
      setTestingPrinter(printer.id);
      await api.post(`/api/stores/${storeId}/kitchen-printers/${printer.id}/test`);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('テスト印刷に失敗しました');
    } finally {
      setTestingPrinter(null);
    }
  };

  // ルーティング設定保存
  const handleRoutingSave = async (categoryId, printerId) => {
    try {
      await api.post(`/api/stores/${storeId}/printer-routing`, {
        categoryId,
        printerId,
      });
      fetchRoutingRules();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('ルーティング設定の保存に失敗しました');
    }
  };

  // プリンタータイプのアイコン取得
  const getPrinterTypeIcon = (type) => {
    const printerType = PRINTER_TYPES.find(t => t.id === type);
    return printerType?.icon || <Print />;
  };

  // ステータスチップ
  const getStatusChip = (status) => {
    switch (status) {
      case 'online':
        return <Chip size="small" icon={<CheckCircle />} label="オンライン" color="success" />;
      case 'offline':
        return <Chip size="small" icon={<ErrorIcon />} label="オフライン" color="error" />;
      default:
        return <Chip size="small" label="不明" color="default" />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Kitchen />
        キッチンプリンター設定
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          保存しました
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab label="プリンター管理" />
        <Tab label="振り分け設定" />
        <Tab label="印刷設定" />
      </Tabs>

      {/* プリンター管理タブ */}
      {activeTab === 0 && (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1">登録済みプリンター</Typography>
            <Box>
              <Button startIcon={<Refresh />} onClick={fetchPrinters} sx={{ mr: 1 }}>
                更新
              </Button>
              <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
                プリンター追加
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>プリンター名</TableCell>
                  <TableCell>タイプ</TableCell>
                  <TableCell>モデル</TableCell>
                  <TableCell>接続</TableCell>
                  <TableCell>ステータス</TableCell>
                  <TableCell>最終印刷</TableCell>
                  <TableCell align="right">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {printers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      プリンターが登録されていません
                    </TableCell>
                  </TableRow>
                ) : (
                  printers.map((printer) => (
                    <TableRow key={printer.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getPrinterTypeIcon(printer.type)}
                          {printer.name}
                          {!printer.isActive && (
                            <Chip size="small" label="無効" color="default" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {PRINTER_TYPES.find(t => t.id === printer.type)?.name || printer.type}
                      </TableCell>
                      <TableCell>
                        {PRINTER_MODELS.find(m => m.id === printer.model)?.name || printer.model}
                      </TableCell>
                      <TableCell>
                        {printer.connectionType === 'lan' || printer.connectionType === 'wifi'
                          ? `${printer.ipAddress}:${printer.port}`
                          : CONNECTION_TYPES.find(c => c.id === printer.connectionType)?.name}
                      </TableCell>
                      <TableCell>{getStatusChip(printer.status)}</TableCell>
                      <TableCell>{printer.lastPrint || '-'}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleTestPrint(printer)}
                          disabled={testingPrinter === printer.id}
                          title="テスト印刷"
                        >
                          {testingPrinter === printer.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Print />
                          )}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(printer)}
                          title="編集"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(printer)}
                          color="error"
                          title="削除"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* 振り分け設定タブ */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              カテゴリ別プリンター振り分け
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              商品カテゴリごとに印刷先のプリンターを設定します。
            </Typography>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>カテゴリ</TableCell>
                    <TableCell>印刷先プリンター</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories.map((category) => {
                    const currentRule = routingRules.find(r => r.categoryId === category.id);
                    return (
                      <TableRow key={category.id}>
                        <TableCell>{category.name}</TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 200 }}>
                            <Select
                              value={currentRule?.printerId || ''}
                              onChange={(e) => handleRoutingSave(category.id, e.target.value)}
                              displayEmpty
                            >
                              <MenuItem value="">
                                <em>印刷しない</em>
                              </MenuItem>
                              {printers.filter(p => p.isActive).map((printer) => (
                                <MenuItem key={printer.id} value={printer.id}>
                                  {getPrinterTypeIcon(printer.type)}
                                  <Box sx={{ ml: 1 }}>{printer.name}</Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* 印刷設定タブ */}
      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              キッチン伝票印刷設定
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Switch defaultChecked />}
                  label="注文時に自動印刷"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Switch defaultChecked />}
                  label="追加注文を別伝票で印刷"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Switch />}
                  label="価格を表示"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Switch defaultChecked />}
                  label="注文番号を大きく表示"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Switch defaultChecked />}
                  label="テーブル番号を表示"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Switch defaultChecked />}
                  label="印刷時に音を鳴らす"
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>文字サイズ</InputLabel>
                  <Select defaultValue="medium" label="文字サイズ">
                    <MenuItem value="small">小</MenuItem>
                    <MenuItem value="medium">中</MenuItem>
                    <MenuItem value="large">大</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="印刷枚数"
                  defaultValue={1}
                  inputProps={{ min: 1, max: 5 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" sx={{ mt: 2 }}>
                  設定を保存
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* プリンター追加/編集ダイアログ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPrinter ? 'プリンターを編集' : 'プリンターを追加'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="プリンター名"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="例: キッチンプリンター1"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>タイプ</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  label="タイプ"
                >
                  {PRINTER_TYPES.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {type.icon}
                        {type.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>モデル</InputLabel>
                <Select
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  label="モデル"
                >
                  {PRINTER_MODELS.map((model) => (
                    <MenuItem key={model.id} value={model.id}>
                      {model.name} ({model.width}mm)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>接続方式</InputLabel>
                <Select
                  value={formData.connectionType}
                  onChange={(e) => setFormData({ ...formData, connectionType: e.target.value })}
                  label="接続方式"
                >
                  {CONNECTION_TYPES.map((conn) => (
                    <MenuItem key={conn.id} value={conn.id}>
                      {conn.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {(formData.connectionType === 'lan' || formData.connectionType === 'wifi') && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="IPアドレス"
                    value={formData.ipAddress}
                    onChange={(e) => setFormData({ ...formData, ipAddress: e.target.value })}
                    placeholder="192.168.1.100"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="ポート"
                    value={formData.port}
                    onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) })}
                  />
                </Grid>
              </>
            )}
            {formData.connectionType === 'bluetooth' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="MACアドレス"
                  value={formData.macAddress}
                  onChange={(e) => setFormData({ ...formData, macAddress: e.target.value })}
                  placeholder="00:11:22:33:44:55"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="有効にする"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : '保存'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KitchenPrinterSettings;
